<template lang="pug">
.simulator
  //-
  //- Simulator Form
  //-
  section
    .section__body.margin-bottom-triple
      FormSimulator(
        v-model:financial-instrument="financialInstrument",
        v-model:issuance-type="issuanceType",
        v-model:amount="amount"
        @calculate="updateIssuanceFeesAndTotals")

  //-
  //- Simulator Results
  //-
  section#print(v-if="showSimulator && issuanceFees")

    //-
    //- PAGE 1
    //- Initial Fees comparison table
    //-
    .simulation__print
      .section__header.no-margin-bottom
        .section__header__cto {{ $t('simulator.simulation') }}
        h2.section__header__title {{ simulationTitle }}
        .section__header__subtitle
          | {{ $t('simulator.capitalization') }}
          Amount.margin-left-quarter(
            :value="amount"
            :locale="locale"
            currency="EUR"
            is-integer)


      .simulation__logo

    hr

    .section__header
      h2.article__header__title(v-if="issuanceType === 'new'") {{ $t('simulator.listingTitleNew') }}
      h2.article__header__title(v-else)
        | {{ $t('simulator.listingTitleMigration1') }}
        br
        | {{ $t('simulator.listingTitleMigration2') }}
      .article__header__subtitle {{ $t('simulator.listingTitlePayment') }}

    SimulatorTable(
      v-if="issuanceType && issuanceFees"
      :fees="issuanceFees.initialFee.fees"
      :total="issuanceFees.initialFee.total"
      :locale="locale"
      :issuance-type="issuanceType")

    .table__footer {{ $t('simulator.allocate') }}

    //-
    //- PAGE 2
    //- Maintenance Fees comparison table
    //-

    //- .html2pdf__page-break

    .simulation__print.only-print(style="margin-top: 20rem")
      .section__header
        .section__header__cto {{ $t('simulator.simulation') }}
        h2.section__header__title {{ simulationTitle }}
        .section__header__subtitle
          | {{ $t('simulator.capitalization') }}
          Amount.margin-left-quarter(
            :value="amount"
            :locale="locale"
            currency="EUR"
            is-integer)


      .simulation__logo

    hr

    .section__header
      h2.article__header__title {{ $t('simulator.maintenanceTitle') }}
      .article__header__subtitle {{ $t('simulator.maintenanceTitlePeriodicity') }}

    SimulatorTable(
      v-if="issuanceType && issuanceFees"
      :fees="issuanceFees.maintenanceFee.fees"
      :total="issuanceFees.maintenanceFee.total"
      :locale="locale"
      :issuance-type="issuanceType")

    hr.hide--print

    SavingMoney(
      :saving-money="issuanceFees.savingMoney"
      :saving-percent="issuanceFees.savingPercent"
      :locale="locale")

    hr.hide--print

    //-
    //- PAGE 3
    //- Advantages of listing a socimi on portfolio stock exchange
    //-
    .only-print--block(v-if="financialInstrument === 'reit'")
      .simulation__print(style="margin-top: 20rem")
        .section__header
          .section__header__cto {{ $t('simulator.simulation') }}
          h2.section__header__title {{ simulationTitle }}
          .section__header__subtitle
            | {{ $t('simulator.capitalization') }}
            Amount.margin-left-quarter(
              :value="amount"
              :locale="locale"
              currency="EUR"
              is-integer)


        .simulation__logo

      hr

      component(:is="AdventagesListingSocimiPortfolio")

  //-
  //- Fees actions
  //-
  .layout--center.hide--print.simulator__cto.margin-bottom-triple(v-if="showSimulator")
    .simulator__cto__action
      button.button(@click="exportPdf") {{ $t('simulator.printSimulation') }}

    TalkToUs.hide--print(
      v-if="financialInstrument && issuanceType && amount"
      :financial-instrument="financialInstrument",
      :issuance-type="issuanceType",
      :amount="amount"
    )
      .talkToUs__section__title.margin-bottom {{ $t('simulator.modal.title1') }}
      .talkToUs__description.margin-bottom {{ $t('simulator.modal.text1') }}
      .talkToUs__description {{ $t('simulator.modal.text2') }}

  .table__footer
    span.color-brand.margin-right-quarter *
    | {{ $t('simulator.estimateRate') }}

  //-
  //- Confirm modal
  //-
  Modal(
    :visible="showModal"
    @close="closeModal"
  )
    .fee__confirm
      TalkToUs.margin-top.margin-bottom(
        v-if="financialInstrument && issuanceType && amount"
        :financial-instrument="financialInstrument",
        :issuance-type="issuanceType",
        :amount="amount"
        vertical
      )
        Success.fee__confirm__success
        .talkToUs__section__title.margin-bottom {{ $t('simulator.modal.title2') }}
        .talkToUs__description.margin-bottom {{ $t('simulator.modal.text1') }}
        .talkToUs__description {{ $t('simulator.modal.text2') }}
</template>

<script setup lang="ts">
  import { computed } from 'vue'
  import Amount from '~/components/Amount.vue'
  import Modal from '~/components/Modal.vue'
  import Success from '~/components/Success.vue'
  import AdventagesListingSocimiPortfolioEn from '~/features/simulator/content/AdventagesListingSocimiPortfolioEn.vue'
  import AdventagesListingSocimiPortfolioEs from '~/features/simulator/content/AdventagesListingSocimiPortfolioEs.vue'
  import { useFeeSimulationSession } from '../composables/useFeeSimulationSession'
  import FormSimulator from './FormSimulator.vue'
  import SavingMoney from './SavingMoney.vue'
  import SimulatorTable from './SimulatorTable.vue'
  import TalkToUs from './TalkToUs.vue'

  //
  // Composables
  //

  const { locale, t } = useI18n()
  const { $html2pdf } = useNuxtApp()
  const { gtag } = useGtag()

  //
  // Data
  //

  const {
    amount,
    calculate: updateIssuanceFeesAndTotals,
    closeModal,
    exportPdf,
    financialInstrument,
    issuanceFees,
    issuanceType,
    showModal,
    showSimulator,
    simulationTitle,
  } = useFeeSimulationSession({
    html2pdf: $html2pdf,
    locale,
    t,
    adapters: {
      postSimulation: payload =>
        $fetch('/api/simulator', {
          method: 'POST',
          body: payload,
        }),
      trackSimulation: payload => {
        gtag('event', 'simulation', payload)
      },
    },
  })

  //
  // Computed
  //

  const AdventagesListingSocimiPortfolio = computed(() => {
    return locale.value === 'es'
      ? AdventagesListingSocimiPortfolioEs
      : AdventagesListingSocimiPortfolioEn
  })
</script>

<style lang="scss">
  .simulation__logo,
  .only-print,
  .only-print--block {
    display: none;
  }

  .fee__confirm {
    margin-top: simple();
  }

  .fee__confirm__success {
    margin: 0 auto simple();
  }

  .fee__confirm__title {
    font-weight: font-weight(semibold);
    max-width: 25rem;
    margin: 0 auto simple();
    text-align: center;
  }

  .fee__confirm__text {
    font-size: font-size(small1);
    line-height: 1.3rem;
    margin-bottom: half();
  }

  .fee__confirm__footer {
    margin-top: simple();
    margin-bottom: double();
  }

  .simulator__cto {
    display: flex;
    align-items: center;

    & > * {
      &:first-of-type {
        padding-right: simple();
        width: 40%;
      }

      &:last-of-type {
        border-left: 1px solid color(gray7);
        padding-left: double();
        width: 60%;
      }
    }
  }

  .talkToUs__section__title {
    font-weight: font-weight(semibold);
  }

  .talkToUs__description {
    font-size: font-size(small1);
  }

  @include media(palm) {
    .simulator__cto {
      flex-direction: column;
      padding: simple();

      & > * {
        &:first-of-type,
        &:last-of-type {
          width: 100%;
        }

        &:last-of-type {
          border-left: none;
          padding: 0;
          margin-top: simple();
        }
      }
    }

    .simulator__cto__action {
      margin-bottom: triple();
    }
  }

  @media (min-width: 4999px) and (max-width: 5001px) {
    .simulation__print {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;
      margin-top: size(2);

      .section__header {
        text-align: left;
        margin: 0 !important;
      }
    }

    .hide--print {
      display: none;
    }

    .only-print {
      display: flex;
    }

    .only-print--block {
      display: block;
    }

    .simulation__logo {
      display: block;
      position: relative;
      left: 2rem;
      width: 240px;
      height: 60px;
      background-image: url('/images/portfolio_logo.svg');
      background-repeat: no-repeat no-repeat;
      background-size: contain;
      background-position: right center;
    }

    .table {
      max-width: 80rem;
      width: 100%;
    }

    hr {
      padding-top: 0;
      padding-bottom: triple() !important;
    }

    .gradient {
      background: none;
      color: color(brand);
    }

    .section__header {
      padding-left: 0;
      padding-right: 0;
    }

    .section__header__subtitle {
      margin-top: 0;
      font-size: 1.1rem;
      line-height: 1.1rem;
    }

    .section__header__title {
      font-size: 1.4rem;
      font-weight: 600;
      margin-bottom: half();
      line-height: 1.4rem;
    }

    .section__header__cto {
      margin-bottom: quarter();
      font-size: 1rem;
      line-height: 1rem;
    }

    th {
      padding-top: size(0.4);
      padding-bottom: size(0.4);
      vertical-align: middle;
      font-size: 1rem !important;
      line-height: 1.1rem !important;
    }

    td {
      padding-top: size(0.35);
      padding-bottom: size(0.35);
    }

    td:first-of-type {
      white-space: nowrap;
    }

    td:not(:first-of-type) {
      width: 12rem;
    }

    .table__footer {
      margin-bottom: double();
      font-size: 0.9rem;
      line-height: 1.2rem;
    }

    .row--total td:not(:first-of-type) {
      font-size: 1rem !important;
    }

    #print {
      position: relative;

      & > div > .section__header {
        margin-bottom: simple();

        .section__header__title {
          font-size: 1.2rem;
          line-height: 1.2rem;
          margin-bottom: quarter();
        }

        .section__header__subtitle {
          font-size: 1rem;
          line-height: 1rem;
        }
      }
    }
  }
</style>
